/**
 * src/components/Financial/Header.jsx
 * ヘッダー・ナビゲーション 【セグメント入力対応版】
 */

import React, { useState, useEffect, useCallback, memo } from "react";
// DatePicker関連のインポートを削除（標準入力を使用するため）
import {
  MoreVertical,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Clock,
  RefreshCw,
  X,
  Moon,
  Sun,
  Settings,
} from "lucide-react";

/**
 * 共通コントロールパーツ（外部定義 + Memo化）
 * 標準の input type="date" を使用し、年・月・日の個別選択を実現します。
 */
const Controls = memo(function (props) {
  const layout = props.layout || "row";
  const isRow = layout === "row";

  // 標準入力（input type="date"）用のローカルステート
  const [inputValue, setInputValue] = useState(props.transitDate);

  useEffect(
    function () {
      setInputValue(props.transitDate);
    },
    [props.transitDate],
  );

  const itemStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: props.isMobile === true && isRow === false ? "none" : "0 0 auto",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: "bold",
    color: "#64748b",
  };

  // プルダウンや入力欄の共通スタイル
  const inputBaseStyle = {
    padding: "8px",
    borderRadius: "6px",
    border: props.isDarkMode
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid #e2e8f0",
    fontSize: "13px",
    backgroundColor: props.isDarkMode ? "#2d2c33" : "#fff",
    color: props.isDarkMode ? "#fff" : "#333",
    outline: "none",
    colorScheme: props.isDarkMode ? "dark" : "light", // ブラウザ標準アイコンを白くする設定
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: layout,
        gap: isRow === true ? "18px" : "16px",
        alignItems: isRow === true ? "center" : "stretch",
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      {/* 銘柄選択 */}
      <div style={itemStyle}>
        <label style={labelStyle}>銘柄選択</label>
        <select
          value={props.selectedAsset}
          onChange={function (e) {
            props.setSelectedAsset(e.target.value);
          }}
          style={Object.assign({}, inputBaseStyle, { minWidth: "140px" })}
        >
          {(props.assets || []).map(function (asset) {
            return (
              <option
                key={asset.id}
                value={asset.id}
                style={{
                  backgroundColor: props.isDarkMode ? "#2d2c33" : "#fff",
                  color: props.isDarkMode ? "#fff" : "#333",
                }}
              >
                {asset.name}
              </option>
            );
          })}
        </select>
      </div>

      {/* トランジット日 */}
      <div style={itemStyle}>
        <label style={labelStyle}>トランジット日</label>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: props.isDarkMode ? "#2d2c33" : "#fff",
              border: props.isDarkMode
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid #e2e8f0",
              borderRadius: "6px",
              height: "38px",
              overflow: "hidden",
            }}
          >
            <button
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "0 8px",
                display: "flex",
                alignItems: "center",
                color: props.isDarkMode ? "#fff" : "#64748b",
              }}
              onClick={props.onPrevDay}
            >
              <ChevronLeft size={16} />
            </button>
            <input
              type="date"
              value={inputValue}
              onChange={function (e) {
                const val = e.target.value;
                setInputValue(val);
                if (val && val.length === 10) {
                  props.setTransitDate(val);
                }
              }}
              style={{
                border: "none",
                background: "transparent",
                color: props.isDarkMode ? "#fff" : "#333",
                fontSize: "13px",
                textAlign: "center",
                width: "120px",
                cursor: "pointer",
                outline: "none",
                colorScheme: props.isDarkMode ? "dark" : "light",
              }}
            />
            <button
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "0 8px",
                display: "flex",
                alignItems: "center",
                color: props.isDarkMode ? "#fff" : "#64748b",
              }}
              onClick={props.onNextDay}
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            onClick={props.onSetCurrentTime}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px",
              borderRadius: "50%",
              background: props.isDarkMode ? "#2d2c33" : "#fff",
              border: props.isDarkMode
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid #e2e8f0",
              color: props.isDarkMode ? "#fff" : "#2c2d96",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* 時刻 */}
      <div style={itemStyle}>
        <label style={labelStyle}>時刻</label>
        <input
          type="time"
          value={props.transitTime}
          onChange={function (e) {
            props.setTransitTime(e.target.value);
          }}
          style={inputBaseStyle}
        />
      </div>

      {/* タイムゾーン */}
      <div style={itemStyle}>
        <label style={labelStyle}>タイムゾーン</label>
        <select
          value={props.timeZone}
          onChange={function (e) {
            props.setTimeZone(e.target.value);
          }}
          style={inputBaseStyle}
        >
          {Array.from({ length: 25 }, function (_, i) {
            const tz = i - 12;
            const val = "UTC" + (tz >= 0 ? "+" : "") + tz;
            return (
              <option
                key={val}
                value={val}
                style={{
                  backgroundColor: props.isDarkMode ? "#2d2c33" : "#fff",
                  color: props.isDarkMode ? "#fff" : "#333",
                }}
              >
                {val}
              </option>
            );
          })}
        </select>
      </div>

      {isRow === true && <div style={{ flex: 1, minWidth: "0px" }} />}

{/* 修正後：トグルスイッチグループ（モバイル時はアイコンを下に移動） */}
      <div
        style={{
          display: "flex",
          gap: "18px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* ヘリオ/ジオ 切替 */}
        <div
          onClick={function () {
            props.setIsGeocentric(!props.isGeocentric);
          }}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
        >
          <div style={{
            width: "36px", height: "20px", borderRadius: "12px", position: "relative",
            backgroundColor: props.isGeocentric === false ? "#93278f" : "#424242",
          }}>
            <div style={{
              width: "14px", height: "14px", backgroundColor: props.isDarkMode ? "#161618" : "#fff",
              borderRadius: "50%", position: "absolute", top: "3px", transition: "left 0.2s",
              left: props.isGeocentric === false ? "19px" : "3px",
            }} />
          </div>
          <span style={{ fontSize: "12px", color: props.isDarkMode ? "#fff" : "#475569", whiteSpace: "nowrap" }}>
            {props.isGeocentric === false ? "ヘリオセントリック(太陽中心)" : "ジオセントリック(地球中心)"}
          </span>
        </div>

        {/* サイデリアル/トロピカル 切替 */}
        <div
          onClick={function () {
            props.setIsTropical(!props.isTropical);
          }}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
        >
          <div style={{
            width: "36px", height: "20px", borderRadius: "12px", position: "relative",
            backgroundColor: props.isTropical === false ? "#93278f" : "#424242",
          }}>
            <div style={{
              width: "16px", height: "16px", backgroundColor: props.isDarkMode ? "#161618" : "#fff",
              borderRadius: "50%", position: "absolute", top: "3px", transition: "left 0.2s",
              left: props.isTropical === false ? "19px" : "3px",
            }} />
          </div>
          <span style={{ fontSize: "12px", color: props.isDarkMode ? "#fff" : "#475569", whiteSpace: "nowrap" }}>
            {props.isTropical === false ? "サイデリアル(恒星黄道帯)" : "トロピカル(回帰黄道帯)"}
          </span>
        </div>

        {/* PC表示時(Row)のみここにアイコンを表示 */}
        {isRow && (
          <>
            <button
              onClick={function () {
                props.setIsDarkMode(!props.isDarkMode);
              }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: props.isDarkMode ? "#fff" : "#949494",
                display: "flex", alignItems: "center", marginLeft: "5px", padding: "0",
              }}
            >
              {props.isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

          {props.onSettingsClick && (
              <button
                onClick={function() {
                  props.onSettingsClick();
                }}
                style={{
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  padding: "0", 
                  marginLeft: "10px", 
                  background: "none", 
                  border: "none",
                  color: props.isDarkMode ? "#fff" : "#949494", 
                  cursor: "pointer",
                }}
              >
                <Settings size={20} />
              </button>
            )}
          </>
        )}
      </div>

      {/* モバイル表示時(Column)は最下部にアイコンを配置（グレーのボーダー付き） */}
      {!isRow && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "40px",
            marginTop: "10px",
            paddingTop: "20px",
            borderTop: "1px solid #cbd5e1", // グレーのボーダー
            width: "100%",
          }}
        >
          <button
            onClick={function () {
              props.setIsDarkMode(!props.isDarkMode);
              if (props.onAction) props.onAction(); // メニューを閉じる
            }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: props.isDarkMode ? "#fff" : "#949494",
              display: "flex", alignItems: "center", padding: "10px",
            }}
          >
            {props.isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
          </button>

{props.onSettingsClick && (
            <button
              onClick={function() {
                // 1. App.jsx側のactiveSectionを"settings"にする
                props.onSettingsClick();
                // 2. Header.jsx内のモバイルメニュー引き出しを閉じる
                if (props.onAction) props.onAction(); 
              }}
              style={{
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                padding: "10px", 
                background: "none", 
                border: "none",
                color: props.isDarkMode ? "#fff" : "#949494", 
                cursor: "pointer",
              }}
            >
              <Settings size={24} />
            </button>
          )}
        </div>
      )}
    </div>
  );
});
/* --- 修正後：Header.jsx の Header 関数内 --- */
export default function Header({
  selectedAsset,
  setSelectedAsset,
  transitDate,
  setTransitDate,
  transitTime,
  setTransitTime,
  timeZone,
  setTimeZone,
  isGeocentric,
  setIsGeocentric,
  isTropical,
  setIsTropical,
  assets = [],
  onFinish,
  onSetCurrentTime,
  onPrevDay,
  onNextDay,
  commonStyles = {},
  onSettingsClick,
  /* Appから渡される新規プロップス */
  isTrialActive,
  isDarkMode,
  setIsDarkMode,
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarWidth,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(function () {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }
    function handleScroll() {
      setIsScrolled(window.scrollY > 100);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return function () {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const closeMobileMenu = useCallback(function () {
    setIsMobileMenuOpen(false);
  }, []);

  const controlProps = {
    selectedAsset,
    setSelectedAsset,
    transitDate,
    setTransitDate,
    transitTime,
    setTransitTime,
    timeZone,
    setTimeZone,
    isGeocentric,
    setIsGeocentric,
    isTropical,
    setIsTropical,
    assets,
    onFinish,
    onSetCurrentTime,
    onPrevDay,
    onNextDay,
    commonStyles,
    onSettingsClick,onAction: closeMobileMenu,
    isMobile,
    isDarkMode,
    setIsDarkMode,
  };

  const headerBaseStyle = {
    width: "100%",
    zIndex: 1000,
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
  };

  /* 修正：props.XXX を XXX に変更してエラーを解消 */
  const pcFixedStyle = {
    ...headerBaseStyle,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    backgroundColor: isDarkMode
      ? "rgba(26, 25, 30, 0.8)"
      : "rgba(255, 255, 255, 0.8)",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    padding: "10px 20px",
    paddingLeft: "calc(" + sidebarWidth + " + 20px)",
    transition: "background-color 0.3s, backdrop-filter 0.3s, box-shadow 0.3s",
  };

  const pcNormalStyle = {
    ...headerBaseStyle,
    position: "relative",
    backgroundColor: isDarkMode ? "#1a191e" : "#fff",
    borderBottom: isDarkMode
      ? "1px solid rgba(255,255,255,0.05)"
      : "1px solid #e2e8f0",
    padding: "15px 20px",
  };

  return (
    <>
     {/* 修正後：モバイル用設定ボタン（左上固定・正円）と引き出しメニューを一括管理 */}
      {isMobile === true && (
        <>

          {/* 左上の設定ボタン（三点リーダー）：メニューが閉じている時のみ表示 */}
          {isMobileMenuOpen === false && (
            <div
              onClick={function () {
                setIsMobileMenuOpen(true);
              }}
              style={{
                position: "fixed",
                top: "15px",
                left: "15px",
                zIndex: 1500,
                cursor: "pointer",
               backgroundColor: "#ffffff",
          color: "#000000",
                // 正円にするための設定
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                transition: "top 0.3s ease",
              }}
            >
             <MoreVertical size={24} color="#000000" />
            </div>
          )}

          {/* 設定メニューのパネル */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: isDarkMode ? "#1a191e" : "#fff",
              zIndex: 2000,
              transform: isMobileMenuOpen === true ? "translateY(0)" : "translateY(-100%)",
              transition: "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              padding: "20px",
            }}
          >
            <Controls {...controlProps} layout="column" />
            <div
              onClick={closeMobileMenu}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "15px 0 0",
               
                marginTop: "15px",
              }}
            >
              <ChevronUp size={24} color="#64748b" />
            </div>
          </div>
        </>
      )}

      {isMobile === false && (
        <header style={isScrolled === true ? pcFixedStyle : pcNormalStyle}>
          <div
            style={{
              maxWidth: "1450px",
              margin: "0 auto",
              display: "flex",
              width: "100%",
              marginTop: "0"
            }}
          >
            <Controls {...controlProps} layout="row" />
          </div>
        </header>
      )}

{isMobile === true && isMobileMenuOpen === true && (
        <div
          onClick={closeMobileMenu}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 1900,
            backdropFilter: "blur(2px)",
          }}
        />
      )}
    </>
  );
}